import { ConfigApi, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { JiraApi, JiraIssue } from './JiraApi';

const DEFAULT_PROXY_PATH = '/jira/api';
const DEFAULT_API_VERSION = 3;

interface JiraSearchResponse {
  issues: Array<{
    id: string;
    key: string;
    self: string;
    fields: {
      summary: string;
      status: {
        name: string;
        statusCategory: {
          key: string;
        };
      };
      issuetype: {
        name: string;
        iconUrl?: string;
      };
      assignee?: {
        displayName: string;
        avatarUrls?: { '24x24'?: string };
      } | null;
      updated: string;
      labels?: string[];
    };
  }>;
}

export class DefaultJiraClient implements JiraApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly configApi: ConfigApi,
  ) {}

  private get proxyPath(): string {
    return (
      this.configApi.getOptionalString('jira.proxyPath') ?? DEFAULT_PROXY_PATH
    );
  }

  private get apiVersion(): number {
    return (
      this.configApi.getOptionalNumber('jira.apiVersion') ?? DEFAULT_API_VERSION
    );
  }

  /**
   * When jira.cloudId is set the proxy target should be https://api.atlassian.com
   * and requests are routed through the Atlassian platform API:
   *   https://api.atlassian.com/ex/jira/{cloudId}/rest/api/3/...
   * Without a cloudId the proxy target should be the instance base URL directly:
   *   https://chb.atlassian.net/rest/api/3/...
   */
  private get cloudId(): string | undefined {
    return this.configApi.getOptionalString('jira.cloudId');
  }

  /**
   * The human-readable Atlassian instance URL (e.g. https://chb.atlassian.net).
   * Used to build /browse/{key} links in the UI.
   * Required when cloudId is set because issue.self returns the api.atlassian.com
   * host, not the instance host.
   * Falls back to parsing issue.self when not configured (direct-mode only).
   */
  private get instanceBaseUrl(): string | undefined {
    return this.configApi.getOptionalString('jira.baseUrl');
  }

  async getBaseUrl(): Promise<string> {
    const proxyBase = await this.discoveryApi.getBaseUrl('proxy');
    const proxyEndpoint = `${proxyBase}${this.proxyPath}`;
    const cloudId = this.cloudId;
    if (cloudId) {
      return `${proxyEndpoint}/ex/jira/${cloudId}`;
    }
    return proxyEndpoint;
  }

  async searchIssues({
    jql,
    maxResults = 50,
  }: {
    jql: string;
    maxResults?: number;
  }): Promise<JiraIssue[]> {
    const base = await this.getBaseUrl();
    // Atlassian deprecated /rest/api/3/search (May 2024) in favour of
    // /rest/api/3/search/jql. GET is used over POST because POST is currently
    // rejected at the api.atlassian.com gateway with a Spring-style 404.
    // See: https://developer.atlassian.com/cloud/jira/platform/changelog/#CHANGE-2046
    const params = new URLSearchParams({
      jql,
      maxResults: String(maxResults),
      fields: [
        'summary',
        'status',
        'assignee',
        'issuetype',
        'updated',
        'labels',
      ].join(','),
    });
    const response = await this.fetchApi.fetch(
      `${base}/rest/api/${this.apiVersion}/search/jql?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      const text = await response.text();
      let message = `Jira search failed: ${response.status} ${response.statusText} — ${text}`;
      if (response.status === 404 && !this.cloudId) {
        message +=
          ' Hint: If the proxy targets https://api.atlassian.com, set jira.cloudId and jira.baseUrl in app config. If the proxy targets your Jira site (e.g. https://your-site.atlassian.net), omit cloudId.';
      }
      throw new Error(message);
    }

    const data = (await response.json()) as JiraSearchResponse;
    const instanceBase = this.instanceBaseUrl;

    return data.issues.map(issue => {
      // When cloudId is configured, issue.self points to api.atlassian.com which
      // is not a valid browse URL. Use the configured instanceBaseUrl instead.
      const browseBase = instanceBase ?? issue.self.split('/rest/')[0];
      return {
        key: issue.key,
        url: `${browseBase}/browse/${issue.key}`,
        summary: issue.fields.summary,
        type: issue.fields.issuetype.name,
        typeIconUrl: issue.fields.issuetype.iconUrl,
        status: issue.fields.status.name,
        statusCategory: issue.fields.status.statusCategory.key,
        assignee: issue.fields.assignee?.displayName,
        assigneeAvatarUrl: issue.fields.assignee?.avatarUrls?.['24x24'],
        labels: issue.fields.labels ?? [],
        updated: issue.fields.updated,
      };
    });
  }
}
